'use client';

/**
 * useDragResize — make a fixed-position panel movable and resizable.
 *
 * Both conveyor surfaces (the roster picker and the approval popup) were
 * hardcoded `fixed bottom-4 right-4` / centred boxes, so they sat on top of the
 * chat with no way to get them out of the way or to open one up when a task
 * description was long enough to need scrolling.
 *
 * Geometry persists per `key` in localStorage, so a panel stays where Boss put
 * it across reloads. Position is clamped to the viewport on every commit AND on
 * window resize — a panel dragged to the edge of a large display must not become
 * unreachable when the window later shrinks.
 *
 * Pointer events (not mouse) so a trackpad drag and a touch drag take the same
 * path, with setPointerCapture to survive the pointer leaving the handle mid-drag.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface Box { x: number; y: number; w: number; h: number }

interface Options {
  /** localStorage key — geometry is remembered per panel. */
  key: string;
  /** Size on first ever open. */
  defaultW: number;
  defaultH: number;
  /** Where it sits on first ever open, before Boss moves it. */
  anchor?: 'bottom-right' | 'center';
  minW?: number;
  minH?: number;
}

const MARGIN = 8;          // keep at least this much inside the viewport
const GRAB_STRIP = 48;     // px of the panel that must stay on screen horizontally

function clamp(b: Box, minW: number, minH: number): Box {
  const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
  const w = Math.max(minW, Math.min(b.w, vw - MARGIN * 2));
  const h = Math.max(minH, Math.min(b.h, vh - MARGIN * 2));
  return {
    w,
    h,
    // Never let the title bar leave the screen: at least GRAB_STRIP px of the
    // panel stays grabbable, and the top edge can never go above 0 (which would
    // put the drag handle out of reach entirely).
    x: Math.max(GRAB_STRIP - w, Math.min(b.x, vw - GRAB_STRIP)),
    y: Math.max(0, Math.min(b.y, vh - MARGIN * 2)),
  };
}

export function useDragResize({ key, defaultW, defaultH, anchor = 'bottom-right', minW = 320, minH = 160 }: Options) {
  const [box, setBox] = useState<Box | null>(null);   // null until measured client-side
  const drag = useRef<{ mode: 'move' | 'resize'; px: number; py: number; start: Box } | null>(null);
  // Mirror of `box` for the drag handlers. THE DRAG-STOPS-AFTER-ONE-PIXEL BUG:
  // when the handlers closed over `box`, the first setBox re-rendered, gave
  // useCallback a NEW handler identity, and the cleanup effect unsubscribed the
  // OLD listener — the one actually registered on window. The drag died after a
  // single pointermove (~10px). Handlers now have [] deps and read live values
  // through refs, so the listener registered at pointerdown stays valid for the
  // whole gesture.
  const boxRef = useRef<Box | null>(null);
  const cfg = useRef({ key, minW, minH });
  cfg.current = { key, minW, minH };

  // Initial geometry runs in an effect, never during render: window doesn't
  // exist during SSR, and reading it in the initializer would desync hydration.
  useEffect(() => {
    let initial: Box | null = null;
    try {
      const saved = localStorage.getItem(`conveyor:box:${key}`);
      if (saved) {
        const p = JSON.parse(saved);
        if (typeof p?.x === 'number' && typeof p?.w === 'number') initial = p as Box;
      }
    } catch { /* corrupt entry — fall through to the default anchor */ }
    if (!initial) {
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = Math.min(defaultW, vw - MARGIN * 2);
      const h = Math.min(defaultH, vh - MARGIN * 2);
      initial = anchor === 'center'
        ? { x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2), w, h }
        : { x: vw - w - 16, y: vh - h - 16, w, h };
    }
    const c = clamp(initial, minW, minH);
    boxRef.current = c;
    setBox(c);
  }, [key, defaultW, defaultH, anchor, minW, minH]);

  // Keep the ref in step with every state change, including the initial measure.
  useEffect(() => { boxRef.current = box; }, [box]);

  const commit = useCallback((b: Box) => {
    const c = clamp(b, minW, minH);
    boxRef.current = c;
    setBox(c);
    try { localStorage.setItem(`conveyor:box:${key}`, JSON.stringify(c)); } catch { /* private mode / quota */ }
  }, [key, minW, minH]);

  // A panel parked against the right or bottom edge of a big window would sit
  // off-screen after the window shrinks. Re-clamp instead of stranding it.
  useEffect(() => {
    const onResize = () => setBox((b) => {
      if (!b) return b;
      const c = clamp(b, minW, minH);
      boxRef.current = c;
      return c;
    });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minW, minH]);

  // Stable for the lifetime of the hook ([] deps) — see the boxRef note above.
  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const { minW: mw, minH: mh } = cfg.current;
    const dx = e.clientX - d.px, dy = e.clientY - d.py;
    const next = d.mode === 'move'
      ? clamp({ ...d.start, x: d.start.x + dx, y: d.start.y + dy }, mw, mh)
      : clamp({ ...d.start, w: d.start.w + dx, h: d.start.h + dy }, mw, mh);
    boxRef.current = next;
    setBox(next);
  }, []);

  const onPointerUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    // Persist only on release — writing localStorage on every move event would
    // hammer it for the whole duration of the gesture.
    const b = boxRef.current;
    if (b) { try { localStorage.setItem(`conveyor:box:${cfg.current.key}`, JSON.stringify(b)); } catch {} }
  }, [onPointerMove]);

  const begin = useCallback((mode: 'move' | 'resize') => (e: React.PointerEvent) => {
    const start = boxRef.current;
    if (!start || e.button !== 0) return;
    // Don't start a drag from a control inside the title bar (the ↺ reset button).
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    e.preventDefault();
    drag.current = { mode, px: e.clientX, py: e.clientY, start };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  useEffect(() => () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(`conveyor:box:${key}`); } catch {}
    const vw = window.innerWidth, vh = window.innerHeight;
    const w = Math.min(defaultW, vw - MARGIN * 2), h = Math.min(defaultH, vh - MARGIN * 2);
    commit(anchor === 'center'
      ? { x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2), w, h }
      : { x: vw - w - 16, y: vh - h - 16, w, h });
  }, [key, defaultW, defaultH, anchor, commit]);

  return {
    box,
    /** Spread onto the panel root. `null` box → render nothing yet (pre-measure). */
    style: box ? { left: box.x, top: box.y, width: box.w, height: box.h } as React.CSSProperties : undefined,
    /** Spread onto the title bar. */
    dragHandleProps: { onPointerDown: begin('move'), style: { cursor: 'move', touchAction: 'none' } as React.CSSProperties },
    /** Spread onto the corner grip. */
    resizeHandleProps: { onPointerDown: begin('resize'), style: { cursor: 'nwse-resize', touchAction: 'none' } as React.CSSProperties },
    reset,
  };
}
